import { ArrowDropDown } from "@mui/icons-material";
import Comments from "@mui/icons-material/Comment";
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Menu,
    MenuItem,
} from "@mui/material";
import { red } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import { get } from "lodash";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import SaveAndExitButton from "../../../common/js/components/SaveAndExitButton";
import { buildAddress } from "../utils/operations";
import constants from "./constants";

const StyledPageToolbar = styled("div")(({ isStatusTrackerVisible }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    padding: "10px 0px",
    backgroundColor: "#fafafa",
    position: "sticky",
    top: isStatusTrackerVisible ? "105px" : "70px",
    zIndex: 2,
}));

const StyledProgressDiv = styled("div")({
    textAlign: "center",
    width: "5em",
    display: "inline-block",
});

const StyledToolbarHeader = styled("div")({
    paddingRight: "1vw",
});

const StyledToolbarButtons = styled("div")({
    display: "flex",
    alignItems: "center",
    paddingTop: "2vh",
});

const StyledCommentButton = styled(Button)({
    padding: 0,
    margin: 0,
    color: "black",
});

const StyledDeleteButton = styled(Button)({
    paddingLeft: "25px",
    paddingRight: "25px",
    backgroundColor: red[500],
    color: "white",
    "&:hover": {
        backgroundColor: red[800],
    },
});

const StyledMenuSubItem = styled(MenuItem)({
    "&>a": {
        paddingLeft: "30px !important",
    },
});

const VoucherToolbar = (props) => {
    const {
        confirmDialog,
        elementsSaving,
        handleDeleteVoucher,
        handleSave,
        handleSaveAndExit,
        handleSubmit,
        isStatusTrackerVisible,
        numComments,
        toggleComments,
        toggleConfirm,
        userProfile,
        authenticatedUserProfile,
        voucher,
    } = props;

    const navigate = useNavigate();
    const [deleteVoucherDialog, setDeleteVoucherDialog] = useState(false);

    const handleCancel = () => {
        toggleConfirm(null);
    };

    const toggleDeleteVoucherDialog = (showDialog) => {
        setDeleteVoucherDialog(showDialog);
    };

    const brokerSubmitActions = {
        Draft: {
            buttonText: "Submit",
            submit: () => {
                handleSubmitAndRedirect(true, constants.urls.broker_submit(voucher.voucherKey), true);
            },
            variant: "contained",
            confirm: false,
            url: constants.urls.broker_submit,
        },
        "Broker Action Required": {
            buttonText: "Submit",
            submit: () => {
                handleSubmitAndRedirect(true, constants.urls.broker_submit(voucher.voucherKey), true);
            },
            variant: "contained",
            confirm: false,
            url: constants.urls.broker_submit,
        },
    };

    const submitActions = {
        broker: {
            ...brokerSubmitActions,
        },
        evp: {
            ...brokerSubmitActions,
        },
        portlandBrokerAdmin: {
            ...brokerSubmitActions,
            "Portland Review Required": {
                buttonText: "Portland Review Required",
                submit: (callback) => {
                    handleSubmitAndRedirect(true, callback, true);
                },
                variant: "contained",
                options: {
                    "Broker Action Required": constants.urls.reassign_voucher_to_broker,
                    Approve: constants.urls.portland_approval,
                },
            },
        },
        arizonaBrokerAdmin: {
            ...brokerSubmitActions,
            "AZ Review Required": {
                buttonText: "AZ Review Required",
                submit: (callback) => {
                    handleSubmitAndRedirect(true, callback, true);
                },
                variant: "contained",
                options: {
                    "Broker Action Required": constants.urls.reassign_voucher_to_broker,
                    Approve: constants.urls.arizona_approval,
                },
            },
        },
        dataEntry: {
            "Needs Review": {
                buttonText: "Start",
                submit: () => {
                    handleSubmit(false, constants.urls.assign_voucher_for_review(voucher.voucherKey), true);
                },
                confirm: false,
                url: constants.urls.assign_voucher_for_review,
            },
            "In Review": {
                buttonText: "In Review",
                submit: (callback) => {
                    handleSubmitAndRedirect(true, callback, true);
                },
                variant: "contained",
                options: {
                    "Broker Action Required": constants.urls.reassign_voucher_to_broker,
                    "Final Review": constants.urls.approve_voucher,
                },
            },
            "Admin Action Required": {
                buttonText: "Edit",
                submit: () => {
                    handleSubmitAndRedirect(false, constants.urls.assign_voucher_for_review(voucher.voucherKey), true);
                },
                confirm: false,
                url: constants.urls.assign_voucher_for_review,
            },
        },
        dataAdmin: {
            "Final Review": {
                buttonText: "In Review",
                submit: (callback) => {
                    handleSubmitAndRedirect(true, callback, true);
                },
                variant: "contained",
                options: {
                    "Broker Action Required": constants.urls.reassign_voucher_to_broker,
                    "Admin Action Required": constants.urls.reassign_voucher_to_dataentry,
                    Approve: constants.urls.approve_voucher,
                },
            },
        },
        accounting: {
            "Awaiting Payment": {
                buttonText: "Ready For Payment",
                submit: () => {
                    handleSubmitAndRedirect(true, constants.urls.ready_for_payment(voucher.voucherKey), false);
                },
                variant: "contained",
                confirm: false,
                url: constants.urls.ready_for_payment,
            },
        },
    };
    const mainAction = get(submitActions, `${userProfile.role}.${voucher.voucherStatus}`, null);

    const handleSubmitAndRedirect = async (save, callback, bypassConfirm) => {
        const result = await handleSubmit(save, callback, bypassConfirm);

        if (result.success) {
            determineRedirectRoute();
        }
    };
    const handleSaveAndRedirect = async () => {
        const result = await handleSaveAndExit();
        if (result.success) {
            determineRedirectRoute();
        }
    };

    const determineRedirectRoute = () => {
        const role = userProfile.role;
        const dealKey = voucher.deal.dealKey;
        role === "accounting" ? navigate("/vouchers") : navigate(`/deal/${dealKey}`);
    };

    const deleteVoucher = async () => {
        toggleDeleteVoucherDialog(false);
        const result = await handleDeleteVoucher(voucher.voucherKey);
        if (result.success) {
            navigate("/deals");
        }
    };
    const confirm = (url) => {
        toggleConfirm(url(voucher.voucherKey));
    };

    const [mainActionAnchorEl, setMainActionAnchorEl] = useState(null);
    const mainActionDropdown = Boolean(mainActionAnchorEl);

    const handleMainActionDropdown = (event) => {
        setMainActionAnchorEl(event.currentTarget);
    };

    const handleCloseMainActionDropdown = () => {
        setMainActionAnchorEl(null);
    };

    const deletePermission = {
        dataAdmin: [
            "Draft",
            "Awaiting Payment",
            "Awaiting Comp",
            "Ready For Accounting",
            "Closed",
            "Broker Action Required",
            "AZ Review Required",
            "Portland Review Required",
        ],
        dataEntry: [
            "Draft",
            "Awaiting Payment",
            "Awaiting Comp",
            "Broker Action Required",
            "AZ Review Required",
            "Portland Review Required",
        ],
        debtEquity: ["Draft", "Awaiting Payment", "Broker Action Required"],
        propertyManagement: ["Draft", "Awaiting Payment", "Broker Action Required"],
        broker: ["Draft", "Awaiting Payment", "Broker Action Required"],
    };

    const hideDeleteButton = () => {
        const isVoucherBatched = voucher.approvedBy !== null && voucher.approvedDate !== null;
        if (
            (isVoucherBatched && authenticatedUserProfile.role === "dataAdmin") ||
            (!isVoucherBatched &&
                (userProfile.user === get(voucher, "auditCreatedBy") ||
                    userProfile.role === "dataAdmin" ||
                    userProfile.role === "dataEntry"))
        ) {
            return (
                deletePermission[userProfile.role] !== undefined &&
                deletePermission[userProfile.role].includes(voucher.voucherStatus)
            );
        }
    };

    return (
        <StyledPageToolbar isStatusTrackerVisible={isStatusTrackerVisible}>
            <StyledToolbarHeader>
                <h2 className="page-title">{voucher.voucherKey && `Voucher ${voucher.voucherKey}`}</h2>
                <p className="page-subtitle">
                    {buildAddress(get(voucher, "deal.listing.property.primaryAddress"))}
                    {get(voucher, "voucherKey", null) ? " | " : " "}
                    {get(voucher, "deal.propertyType")} {get(voucher, "deal.transactionType")}
                </p>
            </StyledToolbarHeader>
            <div>
                <StyledToolbarButtons>
                    <div>
                        <StyledCommentButton onClick={toggleComments}>
                            <Comments /> Comments ({numComments})
                        </StyledCommentButton>
                    </div>
                    {hideDeleteButton() && (
                        <div>
                            <StyledDeleteButton onClick={() => toggleDeleteVoucherDialog(true)}>
                                Delete
                            </StyledDeleteButton>
                        </div>
                    )}
                    <div>
                        {elementsSaving.saveAndExit ? (
                            <StyledProgressDiv>
                                <CircularProgress size={20} color="primary" />
                            </StyledProgressDiv>
                        ) : (
                            <React.Fragment>
                                <SaveAndExitButton onSave={handleSave} onSaveAndExit={handleSaveAndRedirect} />
                            </React.Fragment>
                        )}
                    </div>
                    <div>
                        {elementsSaving.submit ? (
                            <StyledProgressDiv>
                                <CircularProgress size={20} color="primary" />
                            </StyledProgressDiv>
                        ) : mainAction && mainAction.options ? (
                            <React.Fragment>
                                <Button
                                    id="Main-Action-Menu"
                                    aria-controls={mainActionDropdown ? "Main-Action-Menu" : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={mainActionDropdown ? "true" : undefined}
                                    onClick={handleMainActionDropdown}
                                    variant="contained"
                                    endIcon={<ArrowDropDown />}
                                >
                                    {get(mainAction, "buttonText")}
                                </Button>
                                <Menu
                                    id="Main-Action-Menu"
                                    anchorEl={mainActionAnchorEl}
                                    open={mainActionDropdown}
                                    onClose={handleCloseMainActionDropdown}
                                    anchorOrigin={{
                                        vertical: "bottom",
                                        horizontal: "right",
                                    }}
                                    transformOrigin={{
                                        vertical: "top",
                                        horizontal: "right",
                                    }}
                                >
                                    {Object.keys(get(mainAction, "options", {})).map((x) => {
                                        let url = get(mainAction, `options.${x}`);
                                        return (
                                            <StyledMenuSubItem
                                                style={{ fontSize: "14px" }}
                                                onClick={() => confirm(url)}
                                            >
                                                {x}
                                            </StyledMenuSubItem>
                                        );
                                    })}
                                </Menu>
                            </React.Fragment>
                        ) : mainAction !== null ? (
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => confirm(get(mainAction, `url`))}
                            >
                                {mainAction.buttonText}
                            </Button>
                        ) : (
                            voucher && (
                                <Button disabled variant="contained" color="secondary">
                                    {voucher.voucherStatus}
                                </Button>
                            )
                        )}
                    </div>
                </StyledToolbarButtons>
            </div>
            <Dialog open={confirmDialog.open} maxWidth="xs" aria-labelledby="confirmation-dialog-title">
                <DialogTitle id="confirmation-dialog-title">Continue?</DialogTitle>
                <DialogContent>Are you sure you want to submit this status change?</DialogContent>
                <DialogActions>
                    <Button onClick={handleCancel} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={() => mainAction["submit"](confirmDialog.onSubmit)} color="primary">
                        Ok
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteVoucherDialog} maxWidth="xs" aria-labelledby="confirmation-dialog-title">
                <DialogTitle id="confirmation-dialog-title">Delete?</DialogTitle>
                <DialogContent>
                    You will not be able to recover this voucher after deletion. Are you sure you want to continue?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => toggleDeleteVoucherDialog(false)} color="primary">
                        Cancel
                    </Button>
                    <Button data-testid="deleteVoucherButton" onClick={deleteVoucher} color="primary">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </StyledPageToolbar>
    );
};

export default VoucherToolbar;
